import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

const AUTOPLAY_DELAY = 4000;

const slides = [
  {
    src: '/hero/notebook.webp',
    alt: 'Reparo de Notebook',
    title: 'Reparo de Notebook',
    subtitle: 'Troca de tela, placa-mãe, teclado e upgrades com garantia',
    path: '/servicos/notebook',
  },
  {
    src: '/hero/tv.webp',
    alt: 'Conserto de TV',
    title: 'Conserto de TV',
    subtitle: 'Reparo de imagem, som e placas em todas as marcas',
    path: '/servicos/tv',
  },
  {
    src: '/hero/pc.webp',
    alt: 'Manutenção de PC',
    title: 'Manutenção de PC',
    subtitle: 'Montagem, upgrade, formatação e redes',
    path: '/servicos/pc',
  },
  {
    src: '/hero/microondas.webp',
    alt: 'Manutenção de Microondas',
    title: 'Manutenção de Microondas',
    subtitle: 'Diagnóstico e reparo com peças de qualidade',
    path: '/servicos/microondas',
  },
  {
    src: '/hero/nobreak.webp',
    alt: 'Manutenção de No-breaks',
    title: 'Manutenção de No-breaks',
    subtitle: 'Preventiva e corretiva para energia ininterrupta',
    path: '/servicos/nobreaks',
  },
];

const HeroCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeSlide = slides[activeIndex];

  return (
    <div
      className="w-full max-w-4xl mx-auto rounded-lg xs:rounded-xl md:rounded-2xl 
                 overflow-hidden shadow-large hero-carousel-wrapper
                 ring-1 ring-gray-200/50"
    >
      <div className="relative group">
        <div
          className="absolute top-0 left-0 right-0 z-30 h-0.5 sm:h-1 
                     bg-white/20 pointer-events-none"
          aria-hidden="true"
        >
          <div
            key={`progress-${activeIndex}`}
            className="hero-progress-bar h-full bg-primary-400"
            style={{ animationDuration: `${AUTOPLAY_DELAY}ms` }}
          />
        </div>

        <div
          className="absolute inset-0 bg-gradient-to-b from-black/15 via-transparent 
                     to-transparent z-10 pointer-events-none transition-opacity 
                     duration-300 group-hover:opacity-75"
        />

        <Swiper
          modules={[Autoplay, Pagination, EffectFade]}
          autoplay={{
            delay: AUTOPLAY_DELAY,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          effect="fade"
          loop
          speed={800}
          pagination={{
            clickable: true,
            dynamicBullets: true,
          }}
          onSwiper={(swiper) => setActiveIndex(swiper.realIndex)}
          onRealIndexChange={(swiper) => setActiveIndex(swiper.realIndex)}
          className="w-full hero-carousel"
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={slide.path}>
              <div className="relative overflow-hidden aspect-video">
                <img
                  src={slide.src}
                  alt={slide.alt}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  decoding={index === 0 ? 'sync' : 'async'}
                  fetchPriority={index === 0 ? 'high' : 'low'}
                  className="hero-slide-image w-full h-full object-cover"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="absolute inset-x-0 bottom-0 z-20 pointer-events-none">
          <div
            className="bg-gradient-to-t from-primary-900/95 via-primary-900/75 
                      to-transparent pt-12 xs:pt-16 sm:pt-20 pb-10 xs:pb-12 
                      sm:pb-14 px-4 xs:px-5 sm:px-6 text-left"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
                className="pointer-events-auto max-w-lg"
              >
                <h3
                  className="text-base xs:text-lg sm:text-xl md:text-2xl 
                             font-bold text-white mb-1 xs:mb-1.5"
                >
                  {activeSlide.title}
                </h3>
                <p
                  className="text-xs xs:text-sm sm:text-base text-white/90 
                            mb-3 xs:mb-4 line-clamp-2"
                >
                  {activeSlide.subtitle}
                </p>
                <Link
                  to={activeSlide.path}
                  className="inline-flex items-center gap-1.5 text-xs xs:text-sm 
                           sm:text-base font-semibold text-white bg-white/20 
                           hover:bg-white/30 backdrop-blur-sm px-3 xs:px-4 py-1.5 
                           xs:py-2 rounded-lg border border-white/30 
                           transition-colors duration-200"
                >
                  Saiba mais
                  <span aria-hidden="true">→</span>
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroCarousel;
